import {
  Injectable,
  inject,
  EnvironmentInjector,
  runInInjectionContext,
} from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import {
  Database,
  DatabaseReference,
  ref,
  set,
  update,
  remove,
  push,
  onValue,
  onChildAdded,
  onDisconnect,
  serverTimestamp,
} from "@angular/fire/database";
import { TranslateService } from "@ngx-translate/core";

import { AuthService } from "./auth.service";
import { ClubService } from "./club.service";
import { ThermalPrinterService } from "./thermal-printer.service";
import { buildSeatTicket } from "./escpos-ticket";
import { PaperSizeService } from "./paper-size.service";

// Shared-printer feature: a desktop tab that has a USB-paired printer can
// register itself as a "bridge" for a club. Phones / tablets in the same
// club see the bridge as a menu option and submit logical print requests
// through Firebase. The bridge re-renders the ticket locally and prints.
// Sidesteps mixed-content / LAN-cert problems entirely.
//
// Scope is per-club, not per-tournament: the printer physically lives at
// the venue and serves whichever tournament the club is running tonight.
//
// Data model (all under the existing /clubs/{clubId} subtree, so reads
// inherit the club's existing permissions and ride on the existing
// clubServ.latestClub subscription — no separate Firebase listener):
//   /clubs/{clubId}/printerBridges/{bridgeId} = { friendlyName, registeredBy, registeredAt }
//   /clubs/{clubId}/printQueue/{bridgeId}/{requestId} = { ...payload, requestedBy, requestedAt, status, errorMessage? }
// .onDisconnect on the bridge node handles tab-close cleanup.
//
// Print requests carry the rendering fields (tournName, dispName, table,
// seat, scheduledAt) directly — not raw ESC/POS bytes. The bridge composes
// the ticket via its own buildSeatTicket so a malicious client can't inject
// arbitrary printer commands.

const BRIDGES_PATH = (clubId: string) => `clubs/${clubId}/printerBridges`;
const QUEUE_PATH = (clubId: string, bridgeId: string) =>
  `clubs/${clubId}/printQueue/${bridgeId}`;

const SESSION_BRIDGE_ID_KEY = "bvBridgeId";
const LOCAL_FRIENDLY_NAME_KEY = "bvBridgeFriendlyName";
const REQUEST_CLEANUP_DELAY_MS = 30000;

export interface AvailableBridge {
  bridgeId: string;
  friendlyName: string;
  registeredBy: string;
}

export interface PrintRequestPayload {
  clubName?: string;
  tournName: string;
  scheduledAt: number; // epoch ms
  dispName: string;
  table: number;
  seat: number;
  tableName?: string;
}

export type RequestStatus =
  | { state: "pending" }
  | { state: "printing" }
  | { state: "done" }
  | { state: "failed"; errorMessage?: string };

@Injectable()
export class PrinterBridgeService {
  // This tab's bridge state.
  bridgeId$ = new BehaviorSubject<string | null>(null);
  friendlyName$ = new BehaviorSubject<string | null>(null);

  private readonly mdb = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  private bridgeRef: DatabaseReference | null = null;
  private queueUnsub: (() => void) | null = null;
  private sharedClubId: string | null = null;

  constructor(
    private auth: AuthService,
    private clubServ: ClubService,
    private thermalPrinter: ThermalPrinterService,
    private translate: TranslateService,
    private paperSize: PaperSizeService,
  ) {
    this.friendlyName$.next(this.readStoredFriendlyName());
  }

  // All modular @angular/fire/database calls must run in the injection context
  // (AngularFire zone-stability requirement).
  private run<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  // ---------- Reading: bridges visible right now ----------

  /**
   * Live-derived list of bridges from the existing club subscription.
   * Re-evaluated on each Angular CD pass; returns a new array each call so
   * downstream `*ngFor trackBy` semantics stay clean.
   */
  getBridges(): AvailableBridge[] {
    const club = this.clubServ.latestClub;
    const v = (club && club.printerBridges) || {};
    const list: AvailableBridge[] = Object.keys(v).map((bridgeId) => ({
      bridgeId,
      friendlyName: v[bridgeId].friendlyName || bridgeId,
      registeredBy: v[bridgeId].registeredBy || "",
    }));
    list.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));
    return list;
  }

  // ---------- Writing: become a bridge for a club ----------

  /** Register this tab as a bridge. Caller passes the friendly name. */
  async share(clubId: string, friendlyName: string): Promise<void> {
    if (!friendlyName || !friendlyName.trim()) {
      throw new Error("A printer name is required to share.");
    }
    if (!this.thermalPrinter.isSupported()) {
      throw new Error("WebUSB is not supported in this browser.");
    }
    const device = await this.thermalPrinter.getPairedDevice();
    if (!device) {
      throw new Error("Pair a printer before sharing.");
    }

    const bridgeId = this.getOrCreateBridgeId();
    const bridgeRef = ref(this.mdb, `${BRIDGES_PATH(clubId)}/${bridgeId}`);
    await this.run(() =>
      set(bridgeRef, {
        friendlyName: friendlyName.trim(),
        registeredBy: this.auth.uid || null,
        registeredAt: serverTimestamp(),
      })
    );
    this.run(() => onDisconnect(bridgeRef).remove());

    this.bridgeRef = bridgeRef;
    this.sharedClubId = clubId;
    this.bridgeId$.next(bridgeId);
    this.friendlyName$.next(friendlyName.trim());
    this.writeStoredFriendlyName(friendlyName.trim());

    this.startQueueListener(clubId, bridgeId);
  }

  async unshare(): Promise<void> {
    this.stopQueueListener();
    if (this.bridgeRef) {
      const r = this.bridgeRef;
      this.run(() => onDisconnect(r).cancel());
      await this.run(() => remove(r)).catch(() => {});
      this.bridgeRef = null;
    }
    this.sharedClubId = null;
    this.bridgeId$.next(null);
    // friendlyName persists in localStorage so it pre-fills next time.
  }

  isSelfBridge(bridgeId: string): boolean {
    return this.bridgeId$.value === bridgeId;
  }

  // ---------- Submitting / processing a print request ----------

  async submitPrintRequest(
    clubId: string,
    bridgeId: string,
    payload: PrintRequestPayload,
  ): Promise<string> {
    const queueRef = ref(this.mdb, QUEUE_PATH(clubId, bridgeId));
    const newRef = this.run(() => push(queueRef));
    // Strip undefined keys — Firebase rejects undefined in set().
    const data: any = {
      tournName: payload.tournName,
      scheduledAt: payload.scheduledAt,
      dispName: payload.dispName,
      table: payload.table,
      seat: payload.seat,
      requestedBy: this.auth.uid || null,
      requestedAt: serverTimestamp(),
      status: "pending",
    };
    if (payload.clubName) data.clubName = payload.clubName;
    if (payload.tableName) data.tableName = payload.tableName;
    await this.run(() => set(newRef, data));
    return newRef.key!;
  }

  watchRequest(
    clubId: string,
    bridgeId: string,
    requestId: string,
  ): Observable<RequestStatus> {
    return new Observable<RequestStatus>((observer) => {
      const r = ref(this.mdb, `${QUEUE_PATH(clubId, bridgeId)}/${requestId}`);
      const unsub = this.run(() =>
        onValue(r, (snap) => {
          const v = snap.val();
          if (!v) {
            observer.next({ state: "done" });
            observer.complete();
            return;
          }
          switch (v.status) {
            case "pending":
              observer.next({ state: "pending" });
              break;
            case "printing":
              observer.next({ state: "printing" });
              break;
            case "done":
              observer.next({ state: "done" });
              observer.complete();
              break;
            case "failed":
              observer.next({ state: "failed", errorMessage: v.errorMessage });
              observer.complete();
              break;
          }
        })
      );
      return () => unsub();
    });
  }

  /**
   * Render and print a payload via the locally-paired printer. Used for
   * direct prints (no bridge selected) and for the self-bridge short-circuit.
   * Paper size is a property of the local printer (not the request), so it
   * resolves here from the user's profile and prompts on first connection.
   */
  async renderAndPrint(payload: PrintRequestPayload): Promise<void> {
    const device = await this.thermalPrinter.getPairedDevice();
    if (!device) throw new Error("No printer paired.");
    const paperSize = await this.paperSize.ensure(device);

    const labels = this.translate.instant(["TABLE", "SEAT"]);
    const bytes = await buildSeatTicket({
      clubName: payload.clubName,
      tournName: payload.tournName,
      scheduledAt: new Date(payload.scheduledAt),
      dispName: payload.dispName,
      table: payload.table,
      seat: payload.seat,
      tableName: payload.tableName,
      labels: { table: labels["TABLE"], seat: labels["SEAT"] },
      paperSize,
    });
    await this.thermalPrinter.print(bytes);
  }

  // ---------- Internal: queue listener (only when registered as a bridge) ----------

  private startQueueListener(clubId: string, bridgeId: string): void {
    this.stopQueueListener();
    const r = ref(this.mdb, QUEUE_PATH(clubId, bridgeId));
    this.queueUnsub = this.run(() =>
      onChildAdded(r, async (snap) => {
        const req = snap.val();
        if (!req || req.status !== "pending") return;
        await this.processRequest(clubId, bridgeId, snap.key!, req);
      })
    );
  }

  private stopQueueListener(): void {
    if (this.queueUnsub) {
      this.queueUnsub();
      this.queueUnsub = null;
    }
  }

  private async processRequest(
    clubId: string,
    bridgeId: string,
    requestId: string,
    req: any,
  ): Promise<void> {
    const reqRef = ref(
      this.mdb,
      `${QUEUE_PATH(clubId, bridgeId)}/${requestId}`
    );
    try {
      await this.run(() => update(reqRef, { status: "printing" }));
      await this.renderAndPrint({
        clubName: req.clubName || undefined,
        tournName: req.tournName || "",
        scheduledAt: req.scheduledAt || Date.now(),
        dispName: req.dispName || "",
        table: req.table,
        seat: req.seat,
        tableName: req.tableName || undefined,
      });
      await this.run(() => update(reqRef, { status: "done" }));
      setTimeout(() => {
        this.run(() => remove(reqRef)).catch(() => {});
      }, REQUEST_CLEANUP_DELAY_MS);
    } catch (err: any) {
      const message = (err && err.message) || String(err);
      await this.run(() =>
        update(reqRef, { status: "failed", errorMessage: message })
      ).catch(() => {});
    }
  }

  // ---------- Internal: bridgeId / friendlyName persistence ----------

  private getOrCreateBridgeId(): string {
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(SESSION_BRIDGE_ID_KEY);
    } catch {}
    if (!id) {
      id = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID()
        : `b-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      try {
        sessionStorage.setItem(SESSION_BRIDGE_ID_KEY, id!);
      } catch {}
    }
    return id!;
  }

  private readStoredFriendlyName(): string | null {
    try {
      return localStorage.getItem(LOCAL_FRIENDLY_NAME_KEY);
    } catch {
      return null;
    }
  }

  private writeStoredFriendlyName(name: string): void {
    try {
      localStorage.setItem(LOCAL_FRIENDLY_NAME_KEY, name);
    } catch {}
  }
}
